import React, { useState, useMemo } from "react";
import { useTrainingObjectTypes } from "../../hooks/trainingObjectTypes";
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  TextField,
  useTheme
} from "@mui/material";
import { useTranslation } from "react-i18next";
import Table from "../../components/ReactTable/Table";
import AdminPageLayout from "../../components/FullPageLayout/AdminPageLayout";
import { useAssignments, useCourses } from "../../hooks/assignments";
import { useTableData } from "../../components/ReactTable/useTableData";
import CourseTypeIcon from "../../components/Icons/CourseTypeIcon";
import { Add } from "@mui/icons-material";
import { hoursAndMinutesToString } from "../../utils/datetime";
import CourseFlags from "../../components/Icons/CourseFlags";
import { PropTypes } from "prop-types";
import Breadcrumb from "../../components/Breadcrumb/Breadcrumb";
import useConstant from "use-constant";
import { debounce } from "lodash";
import ConfigureAssignmentModal from "./ConfigureAssignmentModal";
import { useDispatch } from "react-redux";
import { enqueueSuccessSnackbar } from "../../redux/actions/snackbarActions";
import SearchBar from "../../components/SearchBar/SearchBar";
import useModal from "../../hooks/useModal";
import Tooltip from "../../components/Tooltip/Tooltip";
import usePermissions from "../../redux/selectors/usePermissions";

const pageTitles = {
  location: "locations",
  group: "groups",
  position: "positions"
};

const AssignmentModal = ({ onResult, course, referenceId, referenceType }) => {
  const { t } = useTranslation();

  return (
    <ConfigureAssignmentModal
      open={true}
      onClose={() => onResult(null)}
      daysToComplete={course.daysToComplete}
      repeatInterval={course.repeatInterval}
      trainingObjectId={course.id}
      referenceId={referenceId}
      referenceType={referenceType}
      onSaveAssignment={newAssignmentData => onResult(newAssignmentData)}
      courseName={course.name}
      passingScore={course.passingScore}
      duration={hoursAndMinutesToString(
        course.durationHours,
        course.durationMinutes,
        t
      )}
    />
  );
};

const CoursesFilters = ({
  onTypeChange,
  trainingObjectTypeOptions,
  onSearch
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid item xs={12} md={5}>
        <Box minWidth={190}>
          <TextField
            select
            defaultValue="all"
            onChange={ev => onTypeChange(ev.target.value)}
            variant="filled"
            InputProps={{
              disableUnderline: true,
              "data-qa": "add-assignment-course-type-filter"
            }}
            fullWidth
            label={t("administration.courseTypeLabel")}
          >
            {trainingObjectTypeOptions.map(({ value, label }) => (
              <MenuItem value={value} key={value}>
                {label}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Grid>
      <Grid item xs={12} md={7}>
        <SearchBar
          onSearchInput={onSearch}
          filled
          searchIconPosition="end"
          background={theme.palette.inputBackground}
          placeholder={t("search")}
        />
      </Grid>
    </Grid>
  );
};

const columns = t => {
  return [
    {
      Header: t("table.columnHeader.type"),
      accessor: "courseType",
      Cell: ({ cell: { value } }) => <CourseTypeIcon type={value} />,
      id: "courseType",
      style: {
        width: "5%"
      },
      disableSortBy: true
    },

    {
      Header: "Number",
      accessor: "number",
      id: "trainingObjects.number",
      style: {
        width: "5%"
      }
    },

    {
      Header: t("table.columnHeader.title"),
      accessor: "name",
      Cell: ({ row: { original } }) => (
        <Box display="flex" alignItems="center">
          <Box mr={2}>{original.name}</Box>
          {original.flags && <CourseFlags flags={original.flags} />}
        </Box>
      ),
      id: "name"
    },
    {
      Cell: () => (
        <Box className="hover-action">
          <Box my={-1}>
            <Tooltip
              title={t("administration.addAssignment")}
              placement="bottom"
            >
              <IconButton color="primary" size="large">
                <Add />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>
      ),
      disableSortBy: true,
      id: "action",
      style: {
        width: "5%"
      }
    }
  ];
};

const AddAssignment = ({
  referenceType,
  referenceId,
  referenceName,
  onGoBackClick
}) => {
  const { getCourses } = useCourses(referenceType, referenceId);
  const dispatch = useDispatch();
  const { isLoading: isLoadingTypes } = useTrainingObjectTypes("assignable");
  const { createAssignment } = useAssignments();
  const [tableStatus, setTableStatus] = useState(null);
  const { hasPolicyPartnerAdminAccess } = usePermissions();
  const {
    data,
    externalFilters,
    paginationState,
    onStateChange,
    onApplyExternalFilters,
    isFetching
  } = useTableData(getCourses);
  const { t } = useTranslation();

  const memoColumns = useMemo(() => columns(t), []);
  let cols = includeNumberColumn(externalFilters?.type)
    ? memoColumns
    : memoColumns.filter(col => col.Header !== "Number");

  function includeNumberColumn(filter) {
    return (
      (filter === undefined || filter === "policy" || filter === "all") &&
      hasPolicyPartnerAdminAccess
    );
  }
  const openModal = useModal(AssignmentModal);

  const onRowClick = async course => {
    const newAssignmentData = await openModal({
      course,
      referenceType,
      referenceId
    }).result;

    if (newAssignmentData) {
      setTableStatus("creating");
      await createAssignment(newAssignmentData);
      setTableStatus(null);
      dispatch(
        enqueueSuccessSnackbar({
          message: t("administration.createAssignmentSuccess")
        })
      );
      onGoBackClick();
    }
  };

  const onFilterCourses = useConstant(() =>
    debounce(onApplyExternalFilters, 500)
  );

  const { trainingObjectTypeOptions } = useTrainingObjectTypes("assignable");

  return (
    <AdminPageLayout
      title={
        <Breadcrumb
          routes={[
            t(`administration.${pageTitles[referenceType]}`),
            referenceName,
            t("addAssignment")
          ]}
        />
      }
      onGoBackClick={onGoBackClick}
    >
      <Box display="flex" flexDirection="column" mt={3}>
        <CoursesFilters
          onTypeChange={type => onFilterCourses({ type })}
          trainingObjectTypeOptions={trainingObjectTypeOptions}
          onSearch={searchName => onFilterCourses({ searchName })}
        />
        <Table
          data={data}
          columns={cols}
          onStateChange={onStateChange}
          paginationState={paginationState}
          paginated={true}
          loading={isFetching || tableStatus === "creating" || isLoadingTypes}
          autoResetRowState={false}
          cellSize="medium"
          onRowClick={({ original }) => onRowClick(original)}
        />
      </Box>
    </AdminPageLayout>
  );
};

export default AddAssignment;

AddAssignment.propTypes = {
  referenceType: PropTypes.oneOf(["location", "position", "group", "special"]),
  referenceId: PropTypes.string
};
